/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateGeneral } from '../../../../../state/thunks/commenting';
// material
import { Switch, TextField, Control, Group, InlineMessage, TextAreaField } from '@insticator/insticator-ui';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller'
import ExpandableSection from '../../../../../material/expandablesection/expandablesection'
import SectionDescription from '../../../../../material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription'
import MarkdownPreview from '../../../../../material/markdownpreview/markdownpreview'
// content
// styles
import injectSheet from 'react-jss'
import styles from './generalsection_styles'

/* ========== ~~~~~~~~~~ COMMENTING CONFIG: GENERAL SECTION ~~~~~~~~~~ ========== */
const GeneralSection = injectSheet(styles)((props) => {
  const dispatch = useDispatch();

  const { mocked } = useSelector(state => state.session);
  const {
    data: { general: configGeneral, commentingUnitConfigurationUUID },
    isUpdating
  } = useSelector(state => state.commenting.commentingConfig)

  const [general, setGeneral] = useState(configGeneral)
  const [isOpened, setIsOpened] = useState(false)

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async () => {
    const response = await dispatch(updateGeneral({ commentingUnitConfigurationUUID, general }))
    setIsOpened(false)
  })

  const handleCancel = () => {
    setIsOpened(false)
    setGeneral(configGeneral)
  }

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(general) === JSON.stringify(configGeneral));
    return isFieldsChanged
  }, [general, configGeneral])

  const isConfigValid = useMemo(() => {
    const isNameValid = !!general.name
    const isClientMessageValid = !general.isClientMessageEnabled || !!general.clientMessage
    return isNameValid && isClientMessageValid
  }, [general])

  const handleGeneralConfigChange = (property, value, isAdvanced = false) => {
    if (isAdvanced) {
      setGeneral({
        ...general,
        advancedSettings: { ...general.advancedSettings, [property]: value }
      })
    }
    else setGeneral({ ...general, [property]: value })
  }

  const controller = (
    <ConfigController
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.general}
      disableSave={!isChanged || !isConfigValid}
      title='General'
      description={<SectionDescription
        main='Font Configurations'
        additional='Enable External Page ID, +2 more'
      />}
    />
  );

  return (
    <ExpandableSection
      className={`generalsection ${props.classes.generalsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.general}
      disableSave={!isChanged || !isConfigValid}
    >
      {/* GENERAL CONFIGURATIONS */}
      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription
            title={'Integration name'}
          // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
          />
          <TextField
            type={'text'}
            required={true}
            name={'integrationName'}
            valid={!!general.name}
            value={general.name}
            handleChange={e => handleGeneralConfigChange('name', e.target.value)}
            placeholder={'website.com'}
          />
          <SectionGroup row>
            <SectionGroupDescription
              title={'Enable External Page ID Functionality'}
              description={general.pageIDCopy}
            />
            <Switch
              handleClick={() => handleGeneralConfigChange('isPageIDEnabled', !general.isPageIDEnabled)}
              on={general.isPageIDEnabled}
              variant='secondary'
            />
          </SectionGroup>
          <InlineMessage
            key={'meow'}
            display={true}
            name={'headercode'}
            variant={'neutral'}
            icon={'exclamation-triangle'}
            title={null}
            message={<span>Enabling or disabling External Page ID affects the embed codes for the Commenting Unit and for the Comments Counter.</span>}
            close={false}
            handleClose={null} />
        </SectionGroup>
      </SectionCard>
      <SectionCard>
        {/* https://insticatorinc.atlassian.net/browse/PROSUPPORT-373 - Remove promoted comments and allow users tto turn off replies */}
        {/* <SectionGroup row>
            <SectionGroupDescription
                title={'Promoted Comments'}
                description={'Enable pinning of a comment to the top of the section.'}
                // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
            />
            <Switch
                
                handleClick={() => handleGeneralConfigChange('isPromoteCommentsEnabled', !general.isPromoteCommentsEnabled)}
                on={general.isPromoteCommentsEnabled}
                variant='secondary'
            />
        </SectionGroup>

        <SectionGroup row>
            <SectionGroupDescription
                title={'Allow users to turn off replies'}
                description={'Users can choose to turn off replies to their comments.'}
            />
            <Switch
                handleClick={() => handleGeneralConfigChange('isTurnOffRepliesEnabled', !general.isTurnOffRepliesEnabled)}
                on={general.isTurnOffRepliesEnabled}
                variant={'secondary'}
            />
        </SectionGroup> */}

        <SectionGroup row>
          <SectionGroupDescription
            title={'«Active on page» indicator'}
            description={'Shows what users are currently active on the page.'}
          />
          <Switch
            handleClick={() => handleGeneralConfigChange('isActiveUsersIndicatorEnabled', !general.isActiveUsersIndicatorEnabled)}
            on={general.isActiveUsersIndicatorEnabled}
            variant={'secondary'}
          />
        </SectionGroup>

        {general.isActiveUsersIndicatorEnabled && <SectionGroup secondary>
          <SectionGroupDescription
            title={'Indicator title'}
          />
          <TextField
            type={'text'}
            name={'activeUsersIndicatorTitle'}
            required={true}
            handleChange={e => handleGeneralConfigChange('activeUsersIndicatorTitle', e.target.value)}
            value={general.activeUsersIndicatorTitle}
            placeholder={'Active on Page'}
          />
        </SectionGroup>}
      </SectionCard>

      {/* FONT CONFIGURATIONS */}
      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription
            title='Font configurations'
            description='Font settings for better accessibility.'
          />
        </SectionGroup>

        <SectionGroup secondary>
          <SectionGroupDescription title='Font size' />
          <Group name={'controls'} >
            <Control text={'Regular'} value={15} handleSelect={val => handleGeneralConfigChange('fontSize', val)} selected={general.fontSize === 15} variant={'secondary'} addCheckWhenSelected={true} />
            <Control text={'Comfortable'} value={16} handleSelect={val => handleGeneralConfigChange('fontSize', val)} selected={general.fontSize === 16} variant={'secondary'} addCheckWhenSelected={true} />
            <Control text={'Accessible'} value={17} handleSelect={val => handleGeneralConfigChange('fontSize', val)} selected={general.fontSize === 17} variant={'secondary'} addCheckWhenSelected={true} />
          </Group>
        </SectionGroup>

        <SectionGroup secondary>
          <SectionGroupDescription title='Font weight' />
          <Group name={'controls'} >
            <Control text={'Regular'} value={400} handleSelect={val => handleGeneralConfigChange('weight', val)} selected={general.weight === 400} variant={'secondary'} addCheckWhenSelected={true} />
            <Control text={'Medium'} value={500} handleSelect={val => handleGeneralConfigChange('weight', val)} selected={general.weight === 500} variant={'secondary'} addCheckWhenSelected={true} />
          </Group>
        </SectionGroup>
      </SectionCard>

      <SectionCard>


        <SectionGroup row>
          <SectionGroupDescription
            title={'Message to End-Users'}
            description={'Talk directly to your commenting users to keep them updated on important information.'}
          />
          <Switch
            handleClick={() => {
              handleGeneralConfigChange('isClientMessageEnabled', !general.isClientMessageEnabled)
            }}
            on={general.isClientMessageEnabled}
            variant={'secondary'}
          />
        </SectionGroup>

        {general.isClientMessageEnabled && <>
          <InlineMessage
            key={'meow'}
            display={true}
            name={'headercode'}
            variant={'neutral'}
            icon={'exclamation-triangle'}
            title={null}
            message={<span className='client-message-notification'>
              This textbox supports markdown syntax for 
              {' '}<a href='https://www.markdownguide.org/basic-syntax/#headings' target='_blank'>headings</a>, 
              {' '}<a href='https://www.markdownguide.org/basic-syntax/#bold' target='_blank'>bold</a>, 
              {' '}<a href='https://www.markdownguide.org/basic-syntax/#italic' target='_blank'>italics</a>, and 
              {' '}<a href='https://www.markdownguide.org/basic-syntax/#links' target='_blank'>links</a>.
            </span>}
            close={false}
            handleClose={null}
          />

          <SectionGroup secondary>
            <TextAreaField
              type={'text'}
              name={'clientMessage'}
              required={true}
              valid={!!general.clientMessage}
              value={general.clientMessage || ''}
              handleChange={e => {
                handleGeneralConfigChange('clientMessage', e.target.value)
              }}
              placeholder={'Enter the message'}
              maxLength={5000}
            />
          </SectionGroup>

          {!!general.clientMessage && <SectionGroup secondary>
            <SectionGroupDescription
              description='Preview of your message:'
            />
            <MarkdownPreview
              value={general.clientMessage || ''}
            />
          </SectionGroup>
          }
        </>
        }
      </SectionCard>


      {/* ADVANCED SETTINGS */}
      {mocked &&
        <SectionCard title='ADVANCED SETTINGS'>
          <SectionGroup>
            <SectionGroupDescription
              title='Group'
              description='Choose a group for this integration.'
            />
            <Group name={'controls advance'}>
              <Control text={'Demo'}
                value={'DEMO'}
                selected={general.advancedSettings.group === 'DEMO'}
                handleSelect={value => handleGeneralConfigChange('group', value, true)}
                variant={'secondary'}
                addCheckWhenSelected={true}
              />
              <Control text={'Customer'}
                value={'CUSTOMER'}
                selected={general.advancedSettings.group === 'CUSTOMER'}
                handleSelect={value => handleGeneralConfigChange('group', value, true)}
                variant={'secondary'}
                addCheckWhenSelected={true}
              />
              <Control text={'Internal'}
                value={'INTERNAL'}
                selected={general.advancedSettings.group === 'INTERNAL'}
                handleSelect={value => handleGeneralConfigChange('group', value, true)}
                variant={'secondary'}
                addCheckWhenSelected={true}
              />
              <Control text={'Test'}
                value={'TEST'}
                selected={general.advancedSettings.group === 'TEST'}
                handleSelect={value => handleGeneralConfigChange('group', value, true)}
                variant={'secondary'}
                addCheckWhenSelected={true}
              />
            </Group>
          </SectionGroup>

          <SectionGroup row>
            <SectionGroupDescription
              title='Require authentication to view commenting unit'
              description='Visitors will be required to sign up and/or sign in to get access to the commenting unit.'
            />
            <Switch
              on={general.advancedSettings.isLoadCommentsButtonEnabled}
              handleClick={() => handleGeneralConfigChange('isLoadCommentsButtonEnabled', !general.advancedSettings.isLoadCommentsButtonEnabled, true)}
              variant='secondary'
            />
          </SectionGroup>
          <SectionGroup row>
            <SectionGroupDescription
              title='Allow users to link Disqus account'
              description='An additional step will be added to the Sign-up flow prompting users to link their Disqus account.'
            />
            <Switch
              on={general.advancedSettings.allowDisqusAccountLink}
              handleClick={() => handleGeneralConfigChange('allowDisqusAccountLink', !general.advancedSettings.allowDisqusAccountLink, true)}
              variant='secondary'
            />
          </SectionGroup>
        </SectionCard>
      }
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default GeneralSection;